<template>
  <v-container>
    <v-row>
      <v-col
        cols="12"
        class="pb-0 pt-0"
      >
        <!-- Toolbar -->
        <v-toolbar
          flat
          text
          height="88px"
        >
          <v-icon left>
            {{ $appConfig.dataset.icon }}
          </v-icon>
          <v-toolbar-title>Dataset : "{{ dataset.name }}"</v-toolbar-title>
          <v-spacer />
          <v-switch
            v-model="dataset.enabled"
            :disabled="dataset.user_permissions !== 'owner_dataset'"
            color="accent"
            :label="'Status : '+(dataset.enabled ? 'enabled' : 'disabled')"
            hide-details
            @change="changeStatus()"
          />
          <v-tooltip
            v-if="dataset.is_readonly"
            top
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="ml-5"
                :ripple="false"
                color="accent"
                text
                outlined
                v-on="on"
              >
                <v-icon left>
                  mdi-database-lock
                </v-icon> Read only
              </v-btn>
            </template>
            <span class="text-center">Data or schema update is not allowed, you can change it in settings tab.</span>
          </v-tooltip>
        </v-toolbar>
        <!-- Toolbar END -->
      </v-col>
      <v-col
        cols="12"
        class="pt-0"
      >
        <!-- tabs START -->
        <v-tabs
          v-model="activeTab"
          fixed-tabs
          background-color="grey lighten-3"
          @change="handleTabsChange"
        >
          <!-- tabs headers START -->
          <v-tab
            v-for="(item, index) in menuItems"
            :key="index"
            class="ml-0"
          >
            {{ item.value }}
          </v-tab>
          <!-- tabs headers END -->

          <!-- tabs content START -->
          <v-tab-item
            v-for="(item, index) in menuItems"
            :key="index"
            :transition="false"
            :reverse-transition="false"
          >
            <template v-if="item.value === 'browser'">
              <dataset-browser
                :dataset="dataset"
                :header-cell-prop="headerCell"
                :loading-prop="loading"
                :re-render-key="DatasetBrowserReRenderKey"
                @updateCloseConstraint="e=>$emit('updateCloseConstraint',e)"
                @loading="changeLoadingValue"
                @tapExecutionFinished="refreshDataset"
              />
            </template>
            <template v-else-if="item.value === 'schema'">
              <!-- We use a key so the component is remounted each time the dataset fields are changed -->
              <dataset-schema
                :key="refreshSchema"
                :dataset="dataset"
                @updateCloseConstraint="e=>$emit('updateCloseConstraint',e)"
                @schemaUpdated="updateLoadedPages('schema')"
              />
            </template>
            <template v-else-if="item.value === 'integration'">
              <dataset-integration
                :dataset="dataset"
                :tabs="tabs"
                :code-to-copy-prop="codeToCopy"
                :current-language-prop="currentLanguage"
                :current-action-prop="currentAction"
                @updateCloseConstraint="e=>$emit('updateCloseConstraint',e)"
                @changeLanguage="changeLanguage"
                @changeAction="changeAction"
              />
            </template>
            <template v-else-if="item.value === 'trigger'">
              <dataset-trigger
                :dataset="dataset"
                :search="search"
                :selected-items="selectedItems"
                @updateCloseConstraint="e=>$emit('updateCloseConstraint',e)"
                @deleteTrigger="deleteTrigger"
                @searchHandler="searchHandler"
                @selectedItemsHandler="selectedItemsHandler"
              />
            </template>
            <template v-else-if="item.value === 'permission'">
              <dataset-permissions
                :dataset="dataset"
                :my-jumbotron="permissionDeniedJumbotron"
                @updateCloseConstraint="e=>$emit('updateCloseConstraint',e)"
                @modifyPermissions="modifyPermissions"
              />
            </template>
            <template v-else-if="item.value === 'monitoring'">
              <dataset-monitoring
                :dataset="dataset"
                :metrics="metrics"
                @updateCloseConstraint="e=>$emit('updateCloseConstraint',e)"
                @refreshMonitoringData="refreshMonitoringData"
              />
            </template>
            <template v-else-if="item.value === 'log'">
              <dataset-taps-log
                :logs="logs"
              />
            </template>
            <template v-else-if="item.value === 'settings'">
              <dataset-settings
                :dataset="dataset"
                :is-new="false"
                :labels-prop="labels"
                @updateCloseConstraint="e=>$emit('updateCloseConstraint',e)"
                @delete="$emit('delete')"
                @close="$emit('close')"
                @datasetUpdated="handleDatasetUpdated()"
              />
            </template>
          </v-tab-item>
          <!-- tabs content END -->
        </v-tabs>
        <!-- tabs END -->
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import DatasetService from './services/DatasetService'
  import DatasetSchema from './DatasetSchema.vue'
  import DatasetSettings from './DatasetCreateUpdate.vue'
  import DatasetPermissions from './DatasetPermissions.vue'
  import DatasetBrowser from './DatasetBrowser.vue'
  import DatasetIntegration from './DatasetIntegration.vue'
  import DatasetTrigger from './DatasetTrigger.vue'
  import DatasetMonitoring from './DatasetMonitoring.vue'
  import NewJumbotron from '../../../UIComponents/Jumbotron.vue'
  import HmdService from '../../../UIComponents/services/HmdService'
  import DatasetTapsLog from "./DatasetTapsLog";
  import SingerService from "./services/SingerService";
  import axios from "axios";

  export default {
    components: {
      DatasetSettings,
      DatasetPermissions,
      DatasetBrowser,
      DatasetSchema,
      DatasetIntegration,
      DatasetTrigger,
      DatasetMonitoring,
      DatasetTapsLog,
      NewJumbotron,
    },
    props: {
      identifier: {
        type: String,
        default: null,
      },
    },
    data: function () {
      return {
        DatasetBrowserReRenderKey: 0,
        dataset: {},
        activeTab: 0,
        menuItems: [],
        error: Number,
        permissionDeniedJumbotron: {
          type: 'accessRequest',
          heading: "You don't have access to this dataset",
          subHeading: 'Ask the owner to give you the permissions to access this dataset. They include :',
          elementList: [
            { text: 'View - grants permissions to read the dataset content' },
            { text: 'Edit - grants permissions to edit content (note: you won\'t be able to modify the schema or the dataset information)' },
            { text: 'Manage - grants permissions to manage the team, edit the schema and general information' },
          ],
          dividerHeading: 'Ask for an access now!',
        },
        metrics: {},
        search: '',
        selectedItems: 'All',
        headerCell: [],
        totalItems: 0,
        loading: false,
        labels: [],
        tabs: {},
        logs: [],
        codeToCopy: '',
        currentLanguage: '',
        currentAction: '',
        // refreshSchema is used to force the update of the dataset-schema component.
        // This "refresh" is done after the change of value, so the value itself is not important.
        refreshSchema: false,
      }
    },
    watch: {
      activeTab: function () {
        this.loadTab(this.activeTab)
      },
    },
    computed: {
      hasOwnerPermissions () {
        return this.dataset.user_permissions.includes('owner_dataset') ||
        this.dataset.user_permissions.includes('owner_project')
      },
      hasWritePermissions () {
        return this.hasOwnerPermissions ||
        this.dataset.user_permissions.includes('read_write_dataset') ||
        this.dataset.user_permissions.includes('read_write_project')
      },
      hasReadPermissions () {
        return this.hasWritePermissions ||
        this.dataset.user_permissions.includes('read_dataset') ||
        this.dataset.user_permissions.includes('read_project')
      },
    },
    mounted: function () {
      this.refreshDataset()
    },
    methods: {
      handleTabsChange (e) {
        if (e == 0) { this.DatasetBrowserReRenderKey += 1 }
      },
      changeStatus () {
        const obj = {
          hmd_id: this.dataset.hmd_id,
          name: this.dataset.name,
          enabled: this.dataset.enabled,
        }
        DatasetService.update(obj, this)
      },
      deleteTrigger (trigger) {
        this.dataset.triggers = this.dataset.triggers.filter(function (el) {
          return el.id !== trigger.id
        })
      },
      modifyPermissions (permissions) {
        this.dataset.permissions = permissions
      },
      searchHandler: function (search) {
        this.search = search
      },
      selectedItemsHandler: function (selectedItems) {
        this.selectedItems = selectedItems
      },
      loadTab (activeTab) {
        var item = this.menuItems[activeTab]
        if (!item.loaded) {
          switch (item.value) {
            case 'browser':

              if (!this.dataset.user_permissions) { return }
              var items = this.dataset.fields
              this.headerCell = []
              for (item in items) {
                this.headerCell.push({
                  value: items[item].name,
                  shown: true,
                  sortable: false,
                  type: items[item].type,
                  query: { operator: items[item].primary_key ? 'equals' : '', values: [] },
                  isFiltering: false,
                  pk: items[item].primary_key,
                  editable: items[item].editable,
                  required: items[item].required,
                })
              }
              break
            case 'schema':
              break
            case 'integration':
              DatasetService.get_api_embed(this.dataset)
                .then(result => {
                  this.tabs = result.data
                  this.currentLanguage = this.tabs.languages[0].language
                  this.currentAction = this.tabs.languages[0].actions[0].name
                  this.codeToCopy = this.tabs.languages[0].actions[0].src
                })
              break
            case 'trigger':
              break
            case 'permission':
              break
            case 'monitoring':
              this.callMonitoring()
              break
            case 'settings':
              HmdService.label_list()
                .then(result => {
                  this.labels = result.data
                })
              break
            default:
              break
          }
        }
        this.menuItems[activeTab].loaded = true
      },
      changeLoadingValue (loadingValue) {
        this.loading = loadingValue
      },
      updateLoadedPages (updatedComponent) {
        switch (updatedComponent) {
          case 'schema':
            // We need to catch an error that can be thrown if the dataset had no schema before,
            // because the browser tab is not present yet
            try {
              this.menuItems.find(x => x.value === 'browser').loaded = false
              // TODO: Is the following line required ?
              // this.menuItems.find(x => x.value === 'schema').loaded=false
            } catch (error) {
              console.log(error)
            }
            this.activeTab = 0
            this.refreshDataset()
            break
          default:
            break
        }
      },
      callMonitoring (refresh = false) {
        console.log('Get metrics')
        console.log(this.$route.params)
        DatasetService.getMetrics(this, this.dataset, refresh)
          .then(result => {
            this.metrics = result.data
            var count = 0
            for (var k in this.metrics) {
              this.metrics[k].options = {
                responsive: true,
                maintainAspectRatio: false,
                title: {
                  display: true,
                  text: this.metrics[k].name,
                },
                legend: {
                  display: true,
                  position: 'right',
                },
              }
              if (this.metrics.hasOwnProperty(k)) {
                for (var j in this.metrics[k].graph_data.datasets) {
                  this.metrics[k].graph_data.datasets[j].fill = false
                  this.metrics[k].graph_data.datasets[j].backgroundColor = this.$appConfig.graphColors[(count + j) % this.$appConfig.graphColors.length].backgroundColor
                  this.metrics[k].graph_data.datasets[j].borderColor = this.$appConfig.graphColors[(count + j) % this.$appConfig.graphColors.length].borderColor
                }
                ++count
              }
            }
          })
      },
      refreshMonitoringData () {
        this.callMonitoring(true)
      },
      changeAction (action) {
        this.currentAction = action
        this.changeCodeToCopy()
      },
      changeLanguage (language) {
        this.currentLanguage = language
        this.changeCodeToCopy()
      },
      changeCodeToCopy () {
        var myLanguageObject = this.tabs.languages.find(x => x.language === this.currentLanguage)
        var myActionObject = myLanguageObject.actions.find(x => x.name === this.currentAction)
        this.codeToCopy = myActionObject.src
      },
      refreshDataset: function () {
        // Choose what type of arg to pass to DatasetService, depends if this.dataset has been initialized or not
        const serviceArg = (Object.keys(this.dataset).length > 0) ? this.dataset : { hmd_id: this.$route.params.identifier }
        return DatasetService.get(serviceArg, this)
          .then(result => {
            this.dataset = result.data
            if (typeof (this.dataset.user_permissions) == 'undefined') {
              this.dataset.user_permissions = null
            }

            // for testing purpose
            // this.dataset.user_permissions = 'owner_dataset'
            // "read_dataset", "read_write_dataset"


            if (this.dataset.user_permissions.includes('owner_dataset')) {
              // Is Admin
              this.menuItems = [
                { value: 'schema', loaded: false },
                { value: 'integration', loaded: false },
                { value: 'trigger', loaded: false },
                { value: 'permission', loaded: false },
                //{ value: 'monitoring', loaded: false },
                { value: 'settings', loaded: false },
              ]
            } else if (this.dataset.user_permissions.includes('read_write_dataset') || this.dataset.user_permissions.includes('read_dataset')) {
              // Read permission
              this.menuItems = [
                { value: 'schema', loaded: false },
                { value: 'integration', loaded: false },
                { value: 'trigger', loaded: false },
                { value: 'permission', loaded: false },
                // { value: 'monitoring', loaded: false },
                // { value: 'settings', loaded: false },
              ]
            }
            else {
              this.menuItems = [
                { value: 'permission', loaded: false },
              ]
            }

            if(this.dataset.tap) {
              this.menuItems.push({value: 'log', loaded: false})
              let cancelToken = new axios.CancelToken(function (c) { self.cancelToken = c })
              SingerService.getlogs(this.dataset.tap.id, {cancelToken: cancelToken})
                .then(result => {
                  this.logs = result.data.logs
              })
            }
            // If Data add permissions...
            if (this.dataset.fields.length > 0 || !this.hasReadPermissions) {
              this.menuItems.splice(0, 0, { value: 'browser', loaded: false })
            }

            for (const item in this.dataset.fields) {
              this.dataset.fields[item].updatedName = this.dataset.fields[item].name
              this.dataset.fields[item].exists = true
            }
            // Creating a new pointer
            this.dataset.updatedFields = JSON.parse(JSON.stringify(this.dataset.fields))

            this.refreshSchema = !this.refreshSchema
            this.DatasetBrowserReRenderKey += 1
            this.loadTab(this.activeTab)

          }).catch(function (error) {
            console.log(error)
          })
      },
      handleDatasetUpdated: function () {
        const t = this
        this.refreshDataset()
          .then(function () {
            t.activeTab = 0
            t.DatasetBrowserReRenderKey += 1
          })
      },
    },
  }
</script>
<style>
.v-window__container.v-window__container--is-active{ height:auto !important;}
</style>
